import React, { useMemo, useState } from 'react';
import {
  Bell,
  Filter,
  FolderPlus,
  History,
  LayoutGrid,
  List,
  Mail,
  MessageSquare,
  MoreVertical,
  Paperclip,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';
import {
  DEFAULT_TEMPLATE_PAGE_SIZE,
  TEMPLATE_TYPE_TABS,
  filterTemplateRows,
  getAvatarInitial,
  getTemplateResultsSummary,
  getTemplateRowsForTab,
  getTemplateTypeTab,
  paginateTemplateRows,
} from '../../utils/workflowTemplatesUtils';

export { TEMPLATE_TYPE_TABS as TEMPLATE_SUB_TABS } from '../../utils/workflowTemplatesUtils';

const ROW_ICON_MAP = {
  mail: Paperclip,
  message: MessageSquare,
  bell: Bell,
};

const TemplateRowIcon = ({ iconKey }) => {
  const Icon = ROW_ICON_MAP[iconKey] || Mail;
  const iconClass = iconKey === 'bell'
    ? 'text-amber-400'
    : 'text-zinc-500';

  return <Icon className={cn('h-4 w-4 shrink-0', iconClass)} aria-hidden="true" />;
};

const UpdatedByCell = ({ name }) => (
  <div className="flex items-center gap-2">
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-700 text-xs font-medium text-zinc-200">
      {getAvatarInitial(name)}
    </span>
    <span className="text-sm text-zinc-300">{name}</span>
  </div>
);

const WorkflowTemplatesPanel = ({
  onCreateTemplate,
  onBuildWithAI,
}) => {
  const [activeTabId, setActiveTabId] = useState('email');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TEMPLATE_PAGE_SIZE);
  const [viewMode, setViewMode] = useState('list');

  const activeTab = getTemplateTypeTab(activeTabId);

  const filteredRows = useMemo(() => {
    const rows = getTemplateRowsForTab(activeTabId);
    return filterTemplateRows(rows, searchQuery);
  }, [activeTabId, searchQuery]);

  const pagination = useMemo(
    () => paginateTemplateRows(filteredRows, page, pageSize),
    [filteredRows, page, pageSize],
  );

  const handleTabChange = (tabId) => {
    setActiveTabId(tabId);
    setSearchQuery('');
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  return (
    <div className="space-y-0" data-testid="workflow-templates-panel">
      <div className="border-b border-zinc-800">
        <div className="flex items-center gap-6 px-1">
          {TEMPLATE_TYPE_TABS.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`template-subtab-${tab.id}`}
                className={cn(
                  'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-emerald-500 text-white'
                    : 'border-transparent text-zinc-400 hover:text-zinc-200',
                )}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 py-5">
        <div>
          <h2 className="text-2xl font-semibold text-white" data-testid="template-library-title">
            {activeTab.title}
          </h2>
          <p className="mt-1 text-sm text-zinc-400" data-testid="template-library-subtitle">
            {activeTab.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-zinc-700 bg-zinc-900/60 text-zinc-200 hover:bg-zinc-800"
            data-testid="template-create-folder-btn"
            onClick={() => toast.info('Create folder is coming soon')}
          >
            <FolderPlus className="mr-2 h-4 w-4" />
            Create folder
          </Button>
          <Button
            type="button"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-500 hover:to-indigo-500"
            data-testid="template-build-with-ai-btn"
            onClick={() => {
              if (onBuildWithAI) onBuildWithAI();
              else toast.info('Build with AI is coming soon');
            }}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Build with AI
          </Button>
          <Button
            type="button"
            className="bg-emerald-500 text-white hover:bg-emerald-600"
            data-testid="template-new-btn"
            onClick={() => onCreateTemplate?.()}
          >
            <Plus className="mr-2 h-4 w-4" />
            New
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-zinc-700 text-zinc-300"
              data-testid="template-history-btn"
              aria-label="Recent template activity"
              onClick={() => toast.info('Recent template activity is coming soon')}
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-zinc-700 text-zinc-300"
              data-testid="template-view-toggle-btn"
              aria-label={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
              onClick={() => setViewMode((mode) => (mode === 'list' ? 'grid' : 'list'))}
            >
              {viewMode === 'list' ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex min-w-[280px] flex-1 flex-wrap items-center justify-end gap-2 sm:flex-none">
            <div className="relative w-full min-w-[220px] sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={activeTab.searchPlaceholder}
                className="border-zinc-800 bg-zinc-950/60 pl-10 text-white placeholder:text-zinc-500"
                data-testid="template-search-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              data-testid="template-filters-btn"
              onClick={() => toast.info('Template filters are coming soon')}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Title</TableHead>
                <TableHead className="text-zinc-400">Type</TableHead>
                <TableHead className="text-zinc-400">Updated On</TableHead>
                <TableHead className="text-zinc-400">Updated By</TableHead>
                <TableHead className="w-12 text-zinc-400" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.rows.length === 0 ? (
                <TableRow className="border-zinc-800">
                  <TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">
                    No templates match your search.
                  </TableCell>
                </TableRow>
              ) : (
                pagination.rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="border-zinc-800 hover:bg-zinc-800/40"
                    data-testid={`template-row-${row.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TemplateRowIcon iconKey={activeTab.icon} />
                        <span className="font-medium text-white">{row.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-300">{row.type}</TableCell>
                    <TableCell className="text-zinc-400">{row.updatedOn}</TableCell>
                    <TableCell>
                      <UpdatedByCell name={row.updatedBy} />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-zinc-400 hover:text-white"
                        data-testid={`template-row-actions-${row.id}`}
                        aria-label={`Actions for ${row.title}`}
                        onClick={() => toast.info('Template actions are coming soon')}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 px-4 py-3">
          <p className="text-sm text-zinc-500" data-testid="template-results-summary">
            {getTemplateResultsSummary(pagination.startIndex, pagination.endIndex, pagination.total)}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300"
              data-testid="template-pagination-prev"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <span
              className="inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 text-sm font-medium text-emerald-400"
              data-testid="template-pagination-page"
            >
              {pagination.page}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300"
              data-testid="template-pagination-next"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((current) => Math.min(pagination.totalPages, current + 1))}
            >
              Next
            </Button>
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger
                className="h-8 w-[108px] border-zinc-700 bg-zinc-900 text-zinc-300"
                data-testid="template-page-size-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-700 bg-zinc-900 text-zinc-200">
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} / page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTemplatesPanel;
